import { Line, curveBasis, line as d3Line } from 'd3-shape';

import { Pos } from './vector';

/**
 * Approximates a point on a cubic Bezier curve from the start position, control point, and a specified length along the curve.
 *
 * Steps:
 * 1. Uses the derivative of the cubic Bezier formula, B'(t), to determine the slope of the curve at the start position.
 * 2. The slope, and the specified length, is used to calculate the approximate point on the curve.
 *
 * Note: This approximation assumes that the length is small enough that the curvature of the Bezier curve does not significantly affect the calculation.
 * This is more accurate for smaller lengths and less accurate for larger lengths where the curve's curvature is more pronounced.
 *
 * @param {Pos} startPos - The starting position of the cubic Bezier curve
 * @param {Pos} ctrlPt - The control point of the cubic Bezier curve used to determine the curve's direction and shape
 * @param {number} length - The distance along the curve from the start position to where the approximate point should be calculated.
 * @returns {Pos} The approximate point on the curve at the specified length from the start position
 */
const approximateBezierPoint = (startPos: Pos, ctrlPt: Pos, length: number): Pos => {
  // B'(t) = derivative of cubic bezier formula
  // Calculate B'(0) = 3(P1 - P0) to get slope at start of curve
  const bPrime0: Pos = {
    xPx: 3 * (ctrlPt.xPx - startPos.xPx),
    yPx: 3 * (ctrlPt.yPx - startPos.yPx),
  };

  // Calculate the magnitude of B'(0) for normalization
  const magBPrime0: number = Math.sqrt(bPrime0.xPx ** 2 + bPrime0.yPx ** 2);

  // Calculate the unit direction vector at start of curve
  const dHat: Pos = {
    xPx: bPrime0.xPx / magBPrime0,
    yPx: bPrime0.yPx / magBPrime0,
  };

  // Calculate the approximate point P at length p from P0
  // Assumes that length is small enough that the curve can be approximated as a straight line
  const approximatePoint: Pos = {
    xPx: startPos.xPx + dHat.xPx * length,
    yPx: startPos.yPx + dHat.yPx * length,
  };

  return approximatePoint;
};

/**
 * Computes a bezier curve path string based on the specified width, height, and padding.
 * Curve travels from top left to top right of the specified width and height.
 * Optionally add padding to the start and end of the currve
 *
 * @param widthPx
 * @param heightPx
 * @param strokeWidth
 * @param paddingStart
 * @param paddingEnd
 * @param includeStrokePadding
 * @returns
 */
const computeBezierCurve = (
  widthPx: number,
  heightPx: number,
  strokeWidth: number,
  paddingStart = 0,
  paddingEnd = 0,
  includeStrokePadding = false,
) => {
  if (!widthPx || !heightPx || !strokeWidth) return '';

  const startPos = {
    xPx: strokeWidth / 2,
    yPx: includeStrokePadding ? strokeWidth / 2 : 0,
  };
  const endPos = {
    xPx: widthPx - strokeWidth / 2,
    yPx: includeStrokePadding ? strokeWidth / 2 : 0,
  };

  const ctrlPt1 = { xPx: widthPx / 6, yPx: heightPx - paddingStart / 3 };
  const ctrlPt2 = { xPx: widthPx * (5 / 6), yPx: heightPx - paddingEnd / 3 };

  // Compute the approximate start and end points of the line considering the padding
  const startPosWithPadding = approximateBezierPoint(startPos, ctrlPt1, paddingStart);
  const endPointWithPadding = approximateBezierPoint(endPos, ctrlPt2, paddingEnd);

  const d3Curve: Line<Pos> = d3Line<Pos>()
    .x((d: Pos) => d.xPx)
    .y((d: Pos) => d.yPx)
    .curve(curveBasis) as Line<Pos>;

  const path = d3Curve([startPosWithPadding, ctrlPt1, ctrlPt2, endPointWithPadding]) || '';
  return path;
};

export { approximateBezierPoint, computeBezierCurve };
